// AWS Cognito Authorization Code + PKCE, via oidc-client-ts. Prod auth only; the
// dev header shim in identity.ts is untouched. The API's decode_and_verify expects
// the Cognito ACCESS token (token_use == "access", client_id claim), so we send
// user.access_token as the Bearer — never the id_token.
//
// oidc-client-ts owns state, nonce, the PKCE code_verifier, and token storage
// (WebStorageStateStore over localStorage), so tokens never live in the URL after
// the code exchange and we don't roll our own crypto.

import {
  UserManager,
  WebStorageStateStore,
  type User,
  type UserManagerSettings,
} from 'oidc-client-ts';

// Config read from Vite build-time env. No real values hardcoded here.
export interface CognitoConfig {
  authority: string; // issuer, e.g. https://cognito-idp.<region>.amazonaws.com/<userPoolId>
  clientId: string;
  scope: string;
  redirectUri: string;
  logoutUri: string; // where Cognito returns after sign-out
  hostedDomain: string; // Hosted UI domain, e.g. https://<prefix>.auth.<region>.amazoncognito.com
  firmProviders: { id: string; label: string }[];
}

function readConfig(): CognitoConfig {
  const env = import.meta.env;
  return {
    authority: env.VITE_COGNITO_AUTHORITY ?? '',
    clientId: env.VITE_COGNITO_CLIENT_ID ?? '',
    scope: env.VITE_COGNITO_SCOPE ?? 'openid email profile',
    redirectUri:
      env.VITE_COGNITO_REDIRECT_URI ?? `${window.location.origin}/auth/callback`,
    logoutUri: env.VITE_COGNITO_LOGOUT_URI ?? `${window.location.origin}/`,
    hostedDomain: env.VITE_COGNITO_HOSTED_DOMAIN ?? '',
    firmProviders: parseFirmProviders(env.VITE_FIRM_SSO_PROVIDERS ?? '[]'),
  };
}

let cachedConfig: CognitoConfig | null = null;
let cachedManager: UserManager | null = null;

export function getCognitoConfig(): CognitoConfig {
  if (!cachedConfig) cachedConfig = readConfig();
  return cachedConfig;
}

// True only when the prod auth env is present enough to build a UserManager. Lets
// the UI show a clear config error instead of redirecting to a broken URL.
export function isCognitoConfigured(): boolean {
  const c = getCognitoConfig();
  return Boolean(c.authority && c.clientId);
}

// Lazily build a single UserManager. Authorization Code flow (response_type
// 'code') => PKCE is used automatically by oidc-client-ts.
function getUserManager(): UserManager {
  if (cachedManager) return cachedManager;
  const c = getCognitoConfig();
  const settings: UserManagerSettings = {
    authority: c.authority,
    client_id: c.clientId,
    redirect_uri: c.redirectUri,
    post_logout_redirect_uri: c.logoutUri,
    response_type: 'code',
    scope: c.scope,
    automaticSilentRenew: true,
    // Cognito issuer metadata doesn't advertise a check_session endpoint, so keep
    // renew via the refresh token / silent flow rather than session monitoring.
    monitorSession: false,
    loadUserInfo: false,
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    stateStore: new WebStorageStateStore({ store: window.localStorage }),
  };
  cachedManager = new UserManager(settings);
  return cachedManager;
}

// Redirect the browser to the Cognito Hosted UI to sign in. `returnTo` rides
// along in the OIDC `state` (which oidc-client-ts keeps in local storage and
// hands back on the callback, so it never travels through the URL) and is what
// lets an emailed link to /portal/<id> still land on /portal/<id>.
export async function login(returnTo?: string): Promise<void> {
  return loginWithProvider(undefined, returnTo);
}

export async function loginWithProvider(provider?: string, returnTo?: string): Promise<void> {
  const target = safeReturnTo(returnTo);
  const providers = getCognitoConfig().firmProviders;
  if (provider && !providers.some((item) => item.id === provider)) throw new Error('Unconfigured sign-in provider');
  const args = {
    ...(target ? { state: { returnTo: target } } : {}),
    ...(provider ? { extraQueryParams: { identity_provider: provider } } : {}),
  };
  await getUserManager().signinRedirect(args);
}

function parseFirmProviders(raw: string): { id: string; label: string }[] {
  try {
    const value: unknown = JSON.parse(raw);
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is { id: string; label: string } =>
      !!item && typeof item === 'object' &&
      typeof item.id === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(item.id) &&
      typeof item.label === 'string' && item.label.trim().length > 0 && item.label.length <= 80,
    );
  } catch {
    return [];
  }
}

// Complete the code exchange after Cognito redirects back to /auth/callback.
// oidc-client-ts validates state + PKCE and stores the user. We then strip the
// query/hash so tokens/authz code never linger in the URL.
export async function handleCallback(): Promise<{
  user: User;
  returnTo: string | null;
}> {
  const user = await getUserManager().signinRedirectCallback();
  cleanUrl();
  const carried = (user.state as { returnTo?: unknown } | undefined)?.returnTo;
  return { user, returnTo: safeReturnTo(carried) };
}

// A base no real deployment can ever have (RFC 2606 reserves .invalid), so a
// value that escapes to another origin changes `url.origin` and is caught.
const RETURN_BASE = 'https://return-to.invalid';
// A backslash is a slash to a URL parser, and tabs/newlines/other control
// characters are STRIPPED before one runs — `/\evil.test` and `/<tab>/evil.test`
// both resolve to another host. Neither belongs in a path we hand the router.
/** A backslash or a control character anywhere in a carried path. */
function hostile(text: string): boolean {
  for (const ch of text) {
    if (ch === '\\') return true;
    const code = ch.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) return true;
  }
  return false;
}

/**
 * Reduce a carried path to something safe to hand to the router, or null.
 *
 * The value rides in the OIDC `state` and lands in a `navigate()` call, so only
 * a same-origin RELATIVE path survives — and it comes back in the form the
 * browser would resolve it to, not as it was written:
 *
 * - `//evil.example` is protocol-relative and resolves to another HOST, so a
 *   bare "starts with /" test is an open redirect;
 * - `/\evil.example` is the same trick with a backslash, which a URL parser
 *   treats as a slash — and `/%5Cevil.example` is that one percent-encoded;
 * - a tab, newline or other control character is removed before resolution
 *   (`/<tab>/evil.example` becomes `//evil.example`), raw or encoded;
 * - `/auth/callback` is refused however it is SPELLED — `/x/../auth/callback`
 *   and `/AUTH/Callback` reach that route too (the router matches a path
 *   case-insensitively) and returning there re-enters the code exchange with a
 *   spent code;
 * - `/` is refused because it carries no destination; the caller's own default
 *   is the right answer, not a redirect.
 *
 * A query and a fragment are kept: an emailed link to /portal/<id>?s=services
 * has to land on that section. The cost of judging a backslash hostile wherever
 * it appears is that a query value containing one is dropped, which is the
 * right way round for a guard whose only job is to refuse a foreign origin.
 */
export function safeReturnTo(value: unknown): string | null {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    return null; // a malformed escape is not a path anything can reason about
  }
  if (hostile(value) || hostile(decoded)) return null;
  let url: URL;
  try {
    url = new URL(value, RETURN_BASE);
  } catch {
    return null;
  }
  if (url.origin !== RETURN_BASE) return null;
  // `pathname` is now normalized: `.`/`..` segments (and their `%2e` spellings)
  // are resolved, so the callback test cannot be walked around.
  // Route matching also decodes path characters; `%61uth/callback` must not
  // evade the callback guard simply because URL.pathname retains the escape.
  const route = new URL(decodeURIComponent(url.pathname), RETURN_BASE);
  if (route.origin !== RETURN_BASE || route.pathname.startsWith('//')) return null;
  const settled = route.pathname.toLowerCase().replace(/\/+$/, '');
  if (settled === '/auth/callback' || settled.startsWith('/auth/callback/')) return null;
  if (url.pathname !== '/scope' && !/^\/(portal|c|customers)\/[0-9a-f-]{36}$/i.test(settled)) return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

function cleanUrl(): void {
  if (window.location.search || window.location.hash) {
    window.history.replaceState({}, document.title, window.location.pathname);
  }
}

// Load the stored user (if any), or null. Does not trigger a network call.
export async function getStoredUser(): Promise<User | null> {
  return getUserManager().getUser();
}

// Keep the mounted React provider and API identity aligned with the SDK. A
// failed renewal alone does not discard a still-valid token; actual expiry does.
export function subscribeUserChanges(onChange: (user: User | null) => void): () => void {
  const events = getUserManager().events;
  const signedOut = () => onChange(null);
  events.addUserLoaded(onChange);
  events.addUserUnloaded(signedOut);
  events.addAccessTokenExpired(signedOut);
  return () => {
    events.removeUserLoaded(onChange);
    events.removeUserUnloaded(signedOut);
    events.removeAccessTokenExpired(signedOut);
  };
}

// The Cognito ACCESS token for the API Bearer, or null if none/expired.
export async function getAccessToken(): Promise<string | null> {
  const user = await getStoredUser();
  if (!user || user.expired) return null;
  return user.access_token ?? null;
}

// Clear local session and redirect to the Cognito logout endpoint so the Hosted
// UI session is also ended. oidc-client-ts posts back to post_logout_redirect_uri.
export async function logout(): Promise<void> {
  const c = getCognitoConfig();
  const manager = getUserManager();
  manager.stopSilentRenew();
  await manager.removeUser();
  if (c.hostedDomain) {
    const url = new URL(`${c.hostedDomain}/logout`);
    url.searchParams.set('client_id', c.clientId);
    url.searchParams.set('logout_uri', c.logoutUri);
    window.location.assign(url.toString());
  } else {
    window.location.assign(c.logoutUri);
  }
}

export type { User };
